// „Nachtrag senden“ (E2, Schritt 5): alles, was der Verkäufer nachreicht, auf
// einen Blick – neue Fotos, neue Notizen, Sprachmemos, Material (seit E3b) und
// Längen (neu oder berichtigt, mit dem neuen Wert; Längen nur, wo es welche
// gibt), berichtigtes „Sonstiges“ – und dann mit
// einem Tipp ans Büro. Wie „Abschließen und senden“ (SendenSeite.tsx, deren
// Karte diese Seite mitbenutzt): Gespeichert ist längst alles am Gerät;
// „Nachtrag senden“ schließt den Nachtrag und stellt seinen Abschluss hinten
// in den Postausgang (arbeitskopie.ts, `nachtragSenden`). Ohne Empfang geht
// das genauso – er wartet dann am Gerät.
//
// Ist noch nichts darin, geht Senden nicht – dafür „Nachtrag abbrechen“.
// Solange ein Sprachmemo für ihn aufgenommen wird, wartet der Knopf. Danach
// zeigt „Nachtrag ist im Büro!“ (GesendetSeite.tsx), wie es steht.
//
// Adresse: #/aufnahme/<kennung>/nachtrag

import { useState } from "react";
import { LAENGEN_ARTEN, STATIONEN, type IchAntwort } from "@happy-energy/kern";
import { AppFuss, AppKopf, fehlerMelden, Ikon, Tipp, toastZeigen, useNetz } from "@happy-energy/oberflaeche";
import { meterZeigen, nachtragSenden, nachtragUnterwegs, nachtragVerwerfen } from "../arbeitskopie.ts";
import { nachtragLeer, nachtragStuecke } from "../postausgang.ts";
import { materialOrdnen, materialText, mitLaengen } from "../material.ts";
import { useMemoAufnahme } from "../memo.ts";
import { aufnahmeLesen, modusVon, type AufnahmeGanz } from "./Uebersicht.tsx";
import { FotosInArbeit, fotosText, memosText, notizenText, ZusammenKarte, type Zeile } from "./SendenSeite.tsx";
import { useUebernahmen } from "./fotos.tsx";
import { SendeStand, useLive, usePostausgang, ZUM_START } from "./gemeinsam.tsx";

/** Der Name einer Station für Menschen – „Dach“, „Verteiler“. */
function stationsName(ganz: AufnahmeGanz, stationId: string): string {
  const nr = ganz.stationen.find((s) => s.id === stationId)?.nr;
  return STATIONEN.find((s) => s.nr === nr)?.name ?? "";
}

/**
 * Was die Karte zeigt – aus der Arbeitskopie, mit denselben Regeln, nach
 * denen der Nachtrag gesendet wird (`nachtragStuecke`): neue Fotos, neue
 * Notizen und Memos, Längen mit ihrem neuen Wert, wo „Sonstiges“ berichtigt ist.
 */
export function nachtragZusammenfassung(ganz: AufnahmeGanz, nachtragId: string): Zeile[] {
  const s = nachtragStuecke(ganz, nachtragId);
  const laengen = s.laengen.map((l) => {
    const art = LAENGEN_ARTEN.find((a) => a.schluessel === l.laengenArt);
    const wie = l.nachtragId === nachtragId ? "neu" : "berichtigt";
    return `${stationsName(ganz, l.stationId)}: ${art?.name ?? l.laengenArt} ${meterZeigen(l.meter)} ${art?.einheit ?? "m"} (${wie})`;
  });
  // Seit E3b: das Material – neu oder in der Menge berichtigt, mit der Station (oder „am Schluss“).
  const material = materialOrdnen(s.materialangaben, ganz.stationen).map((m) => {
    const wie = m.nachtragId === nachtragId ? "neu" : "berichtigt";
    return `${m.stationId ? stationsName(ganz, m.stationId) : "Am Schluss"}: ${materialText(m)} (${wie})`;
  });
  return [
    { was: "Neue Fotos", wert: fotosText(s.fotos) },
    { was: "Neue Notizen", wert: notizenText(s.notizen) },
    { was: "Sprachmemos", wert: memosText(s.memos) },
    { was: "Material", wert: material.join(" · ") || "unverändert" },
    // Längen nur, wo die Besichtigung welche hat (vor dem Update begonnen).
    ...(mitLaengen(ganz.laengen) ? [{ was: "Längen", wert: laengen.join(" · ") || "unverändert" }] : []),
    // Eine berichtigte Station: „Sonstiges“ – seit E3c (S5) an der Station Dach auch Höhe und Zugang (dieselbe Marke).
    {
      was: s.stationen.some((x) => x.art === "dach") ? "Sonstiges, Höhe, Zugang" : "Sonstiges",
      wert: s.stationen.length > 0 ? `berichtigt: ${s.stationen.map((x) => stationsName(ganz, x.id)).join(", ")}` : "unverändert",
    },
  ];
}

function NachtragInhalt({ ich, ganz }: { ich: IchAntwort; ganz: AufnahmeGanz }) {
  const { aufnahme, nachtraege } = ganz;
  const post = usePostausgang();
  const netz = useNetz();
  const memo = useMemoAufnahme();
  const [sendet, setSendet] = useState(false);

  const zurUebersicht = `#/aufnahme/${aufnahme.id}`;
  const zuGesendet = `${zurUebersicht}/nachtrag/gesendet`;
  const modus = modusVon(ganz);
  const nachtragId = modus.art === "nachtrag" ? modus.nachtragId : null;
  const leer = nachtragId ? nachtragLeer(ganz, nachtragId) : true;
  // Ein Memo wird aufgenommen (oder nach dem Stopp noch gespeichert): Es soll mit.
  const memoLaeuft = memo.art !== "aus" || ganz.memos.some((m) => m.nachtragId === nachtragId && m.pruefsumme === null);
  // Seit E3c (S5; O94): Werden noch Fotos übernommen (auch nach dem Verlassen der Station), wartet „Nachtrag senden“.
  const fotosLaufen = useUebernahmen().some((u) => u.aufnahmeId === aufnahme.id);
  const bereit = nachtragId !== null && !leer && !memoLaeuft && !fotosLaufen;
  // Schon gesendet (oder gerade unterwegs): dann zeigt die Seite das statt des Knopfs.
  const gesendet = !nachtragId && (nachtragUnterwegs(nachtraege) ?? nachtraege.at(-1));

  async function senden(id: string) {
    setSendet(true);
    try {
      const ergebnis = await nachtragSenden(id);
      if (ergebnis === "gesendet" || ergebnis === "gesperrt") {
        window.location.hash = zuGesendet;
        return;
      }
      toastZeigen(ergebnis === "leer" ? "Im Nachtrag ist noch nichts." : "Ein Sprachmemo wird noch gespeichert – einen Moment.", "fehler");
    } catch (fehler) {
      fehlerMelden("doku", fehler);
      toastZeigen("Konnte den Nachtrag nicht senden – bitte noch einmal.", "fehler");
    } finally {
      setSendet(false);
    }
  }

  async function abbrechen(id: string) {
    try {
      const ergebnis = await nachtragVerwerfen(id);
      if (ergebnis === "nicht-leer") {
        toastZeigen("Im Nachtrag ist schon etwas – das geht ans Büro mit „Nachtrag senden“.", "fehler");
        return;
      }
      if (ergebnis !== "gesperrt") toastZeigen("Nachtrag abgebrochen");
      window.location.hash = zurUebersicht;
    } catch (fehler) {
      fehlerMelden("doku", fehler);
      toastZeigen("Konnte den Nachtrag nicht abbrechen – bitte noch einmal.", "fehler");
    }
  }

  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        zurueck={{ adresse: zurUebersicht, name: "Übersicht" }}
        titel="Nachtrag"
        rechts={<SendeStand wartend={post.wartend} />}
        gross={!nachtragId ? (gesendet ? "Schon gesendet" : "Kein Nachtrag") : leer ? "Noch nichts dazu" : "Das reichst du nach"}
        unter={`${aufnahme.kunde.name} · ${aufnahme.objekt.strasse}`}
      />
      <main className="app-inhalt">
        {nachtragId && <ZusammenKarte zeilen={nachtragZusammenfassung(ganz, nachtragId)} />}

        {!nachtragId ? (
          <Tipp art="grau" ikon={gesendet ? "haken" : "info"}>
            {gesendet
              ? "Dieser Nachtrag ist schon gesendet – hier gibt es nichts mehr zu senden."
              : "Gerade läuft kein Nachtrag. „Etwas nachreichen“ beginnt einen – unten in der Übersicht."}
          </Tipp>
        ) : leer ? (
          <>
            <Tipp art="grau">
              Noch ist nichts dazugekommen. Öffne die Station, an der etwas fehlt, und gib es dazu – oder brich den Nachtrag ab.
            </Tipp>
            <button type="button" className="knopf-leise" onClick={() => void abbrechen(nachtragId)}>
              Nachtrag abbrechen
            </button>
          </>
        ) : memoLaeuft ? (
          <Tipp art="gelb" ikon="mikro">
            <b>Ein Sprachmemo wird noch aufgenommen oder gespeichert.</b> Es soll mit – senden geht gleich danach.
          </Tipp>
        ) : fotosLaufen ? (
          <FotosInArbeit aufnahmeId={aufnahme.id} />
        ) : !netz ? (
          <Tipp art="gelb" ikon="offline">
            <b>Kein Empfang – kein Problem.</b> Der Nachtrag ist sicher am Gerät und geht von selbst raus, sobald wieder Netz da ist. Die
            App muss dafür nur offen bleiben.
          </Tipp>
        ) : (
          <Tipp ikon="wolke">Was schon gesendet ist, bleibt, wie es ist. Im Büro ist der Nachtrag als neu erkennbar.</Tipp>
        )}
      </main>
      <AppFuss>
        {!nachtragId ? (
          <a href={gesendet ? zuGesendet : zurUebersicht} className="knopf-haupt knopf-block">
            {gesendet ? (
              <>
                <Ikon name="haken" /> Gesendet – Stand ansehen
              </>
            ) : (
              <>
                <Ikon name="links" /> Zur Übersicht
              </>
            )}
          </a>
        ) : (
          <button type="button" className="knopf-haupt knopf-block" disabled={!bereit || sendet} onClick={() => void senden(nachtragId)}>
            <Ikon name="senden" /> {netz ? "Nachtrag senden" : "Senden, sobald Empfang da ist"}
          </button>
        )}
      </AppFuss>
    </div>
  );
}

export function NachtragSeite({ ich, aufnahmeId }: { ich: IchAntwort; aufnahmeId: string }) {
  const ganz = useLive(() => aufnahmeLesen(aufnahmeId), aufnahmeId);
  if (ganz === undefined) return <div className="app-seite" />;
  if (ganz === null) {
    return (
      <div className="app-seite">
        <AppKopf umgebung={ich.umgebung} zurueck={ZUM_START} titel="Nachtrag" gross="Nicht gefunden" />
        <main className="app-inhalt">
          <Tipp art="grau">Diese Besichtigung ist auf diesem Gerät nicht gespeichert.</Tipp>
        </main>
      </div>
    );
  }
  return <NachtragInhalt ich={ich} ganz={ganz} />;
}
